/**
 * Bulk upload helpers for the Upload page.
 *
 * Pure functions only (no API calls, no React): the upload-config and
 * upload-status reply parsers, the step-3 row model, error-code messages,
 * the batch id, the upload concurrency pool and the poll back-off.
 */

import { format } from 'date-fns';

export const DEFAULT_MAX_BULK_FILES = 20;
export const BULK_UPLOAD_CONCURRENCY = 3;
export const POLL_INTERVAL_MS = 5000;
export const HIDDEN_POLL_INTERVAL_MS = 30000;
export const MAX_POLL_BACKOFF_MS = 60000;
const RESUME_WINDOW_MS = 24 * 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// Upload config
// ---------------------------------------------------------------------------

export interface UploadModeConfig {
  /** Many files per upload, processed by the server queue. */
  bulkMode: boolean;
  /** The server queues processing (asyncProcessing), in bulk or single-file mode. */
  serverQueue: boolean;
  maxBulkFiles: number;
}

/** Bulk upload is always on (no switch). Used until the config loads, and if it can't be loaded. */
export const DEFAULT_UPLOAD_MODE: UploadModeConfig = {
  bulkMode: true,
  serverQueue: true,
  maxBulkFiles: DEFAULT_MAX_BULK_FILES,
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** `data` of GET /lab-config/upload-config. */
export function parseUploadConfig(data: unknown): UploadModeConfig {
  if (!isRecord(data)) return DEFAULT_UPLOAD_MODE;
  const { maxBulkFiles } = data;
  return {
    ...DEFAULT_UPLOAD_MODE,
    maxBulkFiles:
      typeof maxBulkFiles === 'number' && Number.isInteger(maxBulkFiles) && maxBulkFiles > 0
        ? maxBulkFiles
        : DEFAULT_MAX_BULK_FILES,
  };
}

// ---------------------------------------------------------------------------
// Upload status (GET /reports/upload-status)
// ---------------------------------------------------------------------------

export type UploadDisplayState =
  | 'queued'
  | 'processing'
  | 'ready'
  | 'needs_attention'
  | 'failed'
  | 'waiting_capacity'
  // Uploaded while the server queue was off and never processed: nothing will happen on its own
  | 'not_started';

export interface ServerReportStatus {
  reportId: string;
  fileName: string;
  fileSize: number;
  uploadedAtMs: number | null;
  state: UploadDisplayState;
  isFinal: boolean;
  error?: string;
  failedPageCount?: number;
  resumesAt?: string;
}

export interface UploadStatusSnapshot {
  batchId: string | null;
  serverTimeMs: number | null;
  reports: ServerReportStatus[];
}

const DISPLAY_STATES: UploadDisplayState[] = [
  'queued',
  'processing',
  'ready',
  'needs_attention',
  'failed',
  'waiting_capacity',
  'not_started',
];
const FINAL_STATES: UploadDisplayState[] = ['ready', 'needs_attention', 'failed', 'not_started'];

const parseTime = (value: unknown): number | null => {
  if (typeof value !== 'string') return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
};

/** The report status as a display state, for a displayState this page does not know. */
function stateFromStatus(status: unknown): UploadDisplayState {
  switch (status) {
    case 'processing':
      return 'processing';
    case 'error':
      return 'failed';
    case 'ready':
    case 'approved':
    case 'rejected':
    case 'published':
      return 'ready';
    default:
      return 'not_started';
  }
}

/**
 * Whether polling should go on for a report: a displayState this page does not
 * know is final (it must never keep step 3 polling), and so is Not started;
 * otherwise the server's isFinal, or the state's own meaning if it is missing.
 */
function isFinalReport(known: UploadDisplayState | undefined, serverIsFinal: unknown): boolean {
  if (!known || known === 'not_started') return true;
  return typeof serverIsFinal === 'boolean' ? serverIsFinal : FINAL_STATES.indexOf(known) !== -1;
}

function toServerReport(raw: Record<string, unknown>): ServerReportStatus {
  const known = DISPLAY_STATES.find((displayState) => displayState === raw.displayState);
  const state = known ?? stateFromStatus(raw.status);
  const { failedPages } = raw;
  let failedPageCount: number | undefined;
  if (typeof failedPages === 'number' && failedPages > 0) failedPageCount = failedPages;
  else if (Array.isArray(failedPages) && failedPages.length > 0) failedPageCount = failedPages.length;

  return {
    reportId: String(raw.reportId),
    fileName: typeof raw.fileName === 'string' ? raw.fileName : '',
    fileSize: typeof raw.fileSize === 'number' ? raw.fileSize : 0,
    uploadedAtMs: parseTime(raw.uploadedAt),
    state,
    isFinal: isFinalReport(known, raw.isFinal),
    error: typeof raw.error === 'string' && raw.error.trim() ? raw.error : undefined,
    failedPageCount,
    resumesAt: typeof raw.resumesAt === 'string' && raw.resumesAt ? raw.resumesAt : undefined,
  };
}

/**
 * Body of GET /reports/upload-status. Returns null when the reply is not the
 * expected shape (for example an older backend without this endpoint).
 */
export function parseUploadStatus(body: unknown): UploadStatusSnapshot | null {
  if (!isRecord(body) || body.success !== true || !isRecord(body.data)) return null;
  const { data } = body;
  if (!Array.isArray(data.reports)) return null;

  const reports = data.reports
    .filter(isRecord)
    .filter((raw) => (typeof raw.reportId === 'string' && raw.reportId !== '') || typeof raw.reportId === 'number')
    .map(toServerReport);

  return {
    batchId: typeof data.batchId === 'string' && data.batchId ? data.batchId : null,
    serverTimeMs: parseTime(data.serverTime),
    reports,
  };
}

/**
 * Return visit: reopen step 3 only for a batch uploaded in the last 24 h
 * (by the server's clock) that still has unfinished reports.
 */
export function isResumableLatestBatch(snapshot: UploadStatusSnapshot | null, nowMs = Date.now()): boolean {
  if (!snapshot || !snapshot.batchId || snapshot.reports.length === 0) return false;
  const uploadTimes = snapshot.reports
    .map((report) => report.uploadedAtMs)
    .filter((ms): ms is number => ms !== null);
  if (uploadTimes.length === 0) return false;
  const now = snapshot.serverTimeMs ?? nowMs;
  if (now - Math.max(...uploadTimes) > RESUME_WINDOW_MS) return false;
  return snapshot.reports.some((report) => !report.isFinal);
}

// ---------------------------------------------------------------------------
// Step-3 rows
// ---------------------------------------------------------------------------

export type BatchRowState =
  | UploadDisplayState
  | 'deleted'
  // Return visit: selected but never acknowledged by the server
  | 'not_uploaded'
  // Return visit while this tab is still sending the file (the nurse navigated away and back)
  | 'upload_pending';

export interface BatchRow {
  /** Stable key: the report id, or a local key for a file that never reached the server. */
  key: string;
  reportId?: string;
  fileName: string;
  fileSize: number;
  state: BatchRowState;
  isFinal: boolean;
  error?: string;
  failedPageCount?: number;
  resumesAt?: string;
  /** From the server; rows without it (still uploading, or upload failed) keep their place. */
  uploadedAtMs?: number;
  /** Position in the nurse's selection, for rows made from a selected file without a report. */
  selectionIndex?: number;
}

export function rowFromServer(report: ServerReportStatus, previous?: BatchRow): BatchRow {
  return {
    key: report.reportId,
    reportId: report.reportId,
    fileName: report.fileName || previous?.fileName || '',
    fileSize: report.fileSize || previous?.fileSize || 0,
    state: report.state,
    isFinal: report.isFinal,
    error: report.error,
    failedPageCount: report.failedPageCount,
    resumesAt: report.resumesAt,
    uploadedAtMs: report.uploadedAtMs ?? previous?.uploadedAtMs,
  };
}

/** A report just accepted by POST /reports/upload/multiple. */
export function initialRowFromUpload(report: { reportId?: unknown; fileName?: unknown; fileSize?: unknown }): BatchRow {
  const reportId = String(report.reportId);
  return {
    key: reportId,
    reportId,
    fileName: typeof report.fileName === 'string' ? report.fileName : '',
    fileSize: typeof report.fileSize === 'number' ? report.fileSize : 0,
    state: 'queued',
    isFinal: false,
  };
}

/** A file whose upload failed: shown as Failed with its reason, never polled. */
export function uploadFailureRow(index: number, file: { name: string; size: number }, error: string): BatchRow {
  return {
    key: `upload-failed-${index}`,
    fileName: file.name,
    fileSize: file.size,
    state: 'failed',
    isFinal: true,
    error,
    selectionIndex: index,
  };
}

/** A selected file the server never acknowledged (the page was left mid-upload). */
export function notUploadedRow(index: number, file: { name: string; size: number }, stillUploading: boolean): BatchRow {
  return {
    key: `not-uploaded-${index}`,
    fileName: file.name,
    fileSize: file.size,
    state: stillUploading ? 'upload_pending' : 'not_uploaded',
    isFinal: !stillUploading,
    selectionIndex: index,
  };
}

/** Rows made from a selected file that has no server report (yet). */
const isLocalFileRow = (row: BatchRow): boolean =>
  !row.reportId && (row.state === 'failed' || row.state === 'not_uploaded' || row.state === 'upload_pending');

/**
 * Apply one upload-status reply. The server is the source of truth: a known
 * report missing from the reply was deleted; reports of this batch the page
 * didn't know about are added. Rows without a report id are kept as they are.
 * The result is ordered by sortBatchRows.
 */
export function mergeBatchRows(previous: BatchRow[], reports: ServerReportStatus[]): BatchRow[] {
  const byId = new Map<string, ServerReportStatus>();
  reports.forEach((report) => byId.set(report.reportId, report));
  const matched = new Set<string>();

  const merged = previous.map((row): BatchRow => {
    if (!row.reportId) return row;
    const report = byId.get(row.reportId);
    if (!report) {
      return {
        key: row.key,
        reportId: row.reportId,
        fileName: row.fileName,
        fileSize: row.fileSize,
        state: 'deleted',
        isFinal: true,
        uploadedAtMs: row.uploadedAtMs,
      };
    }
    matched.add(row.reportId);
    return rowFromServer(report, row);
  });

  reports.forEach((report) => {
    if (matched.has(report.reportId)) return;
    matched.add(report.reportId);
    // The server has a file this page has no report for (its upload reply was lost, or it was
    // sent after the page was left): it replaces that file's Failed / Not uploaded row
    // (same name and size), so the nurse doesn't upload it again
    const lostReply = merged.findIndex(
      (row) => isLocalFileRow(row) && row.fileName === report.fileName && row.fileSize === report.fileSize
    );
    if (lostReply !== -1) merged.splice(lostReply, 1, rowFromServer(report));
    else merged.push(rowFromServer(report));
  });

  return sortBatchRows(merged);
}

/**
 * One order in every view (first visit, return visit, every poll): rows known
 * to the server by upload time, then file name; they are re-ordered among the
 * places they already hold, so rows without a server row keep their place.
 */
export function sortBatchRows(rows: BatchRow[]): BatchRow[] {
  const timed = rows
    .filter((row) => row.uploadedAtMs !== undefined)
    .sort(
      (a, b) =>
        (a.uploadedAtMs as number) - (b.uploadedAtMs as number) || a.fileName.localeCompare(b.fileName)
    );
  return rows.map((row) => (row.uploadedAtMs !== undefined ? (timed.shift() as BatchRow) : row));
}

/**
 * Reports whose /process call this page started itself (the server answered
 * the upload with queued:false): the server can still say Not started until
 * that call has claimed them. `starts` maps each such report to when its call
 * answered (null while it has not). A Not started row is held as Queued, not
 * final, until the call has answered and a status asked for after that answer
 * (`requestedAtMs`) has come back.
 */
export function holdRowsBeingStarted(
  rows: BatchRow[],
  starts: ReadonlyMap<string, number | null>,
  requestedAtMs: number
): BatchRow[] {
  return rows.map((row): BatchRow => {
    if (row.state !== 'not_started' || !row.reportId || !starts.has(row.reportId)) return row;
    const answeredAt = starts.get(row.reportId);
    const stillStarting = answeredAt === null || answeredAt === undefined || requestedAtMs <= answeredAt;
    return stillStarting ? { ...row, state: 'queued', isFinal: false } : row;
  });
}

/** Forgets /process calls that answered before `requestedAtMs`: that status already shows their effect. */
export function forgetAnsweredStarts(starts: Map<string, number | null>, requestedAtMs: number): void {
  starts.forEach((answeredAt, reportId) => {
    if (answeredAt !== null && answeredAt < requestedAtMs) starts.delete(reportId);
  });
}

export interface BatchSummary {
  total: number;
  finalCount: number;
  /** Ready or Needs attention: both open for review. */
  readyCount: number;
  allFinal: boolean;
  allReady: boolean;
}

/** Counts reports only: files that were never uploaded are left out (they are counted by countNotUploaded). */
export function summariseRows(rows: BatchRow[]): BatchSummary {
  const reports = rows.filter((row) => row.state !== 'not_uploaded');
  const finalCount = reports.filter((row) => row.isFinal).length;
  const readyCount = reports.filter((row) => row.state === 'ready' || row.state === 'needs_attention').length;
  return {
    total: reports.length,
    finalCount,
    readyCount,
    allFinal: finalCount === reports.length,
    allReady: reports.length > 0 && reports.every((row) => row.state === 'ready'),
  };
}

export const countNotUploaded = (rows: BatchRow[]): number =>
  rows.filter((row) => row.state === 'not_uploaded').length;

/** While Not uploaded rows are shown, the server is re-checked this often, for this long. */
export const NOT_UPLOADED_RECHECK_INTERVAL_MS = 30000;
export const NOT_UPLOADED_RECHECK_MS = 10 * 60 * 1000;

/**
 * Step-3 polling can stop: every report is final and, when files are listed as
 * Not uploaded, the server has been re-checked for 10 minutes, because a file
 * sent just before the page was left can still land (its report then replaces
 * its Not uploaded row).
 */
export function isPollingDone(rows: BatchRow[], trackingStartedMs: number, nowMs = Date.now()): boolean {
  if (!summariseRows(rows).allFinal) return false;
  return countNotUploaded(rows) === 0 || nowMs - trackingStartedMs >= NOT_UPLOADED_RECHECK_MS;
}

/** Only re-checking for late landings: every report is final and some files are Not uploaded. */
const onlyRecheckingNotUploaded = (rows: BatchRow[]): boolean =>
  countNotUploaded(rows) > 0 && summariseRows(rows).allFinal;

/** "N files were not uploaded. Select them again to upload." */
export function notUploadedMessage(count: number): string {
  return count === 1
    ? '1 file was not uploaded. Select it again to upload.'
    : `${count} files were not uploaded. Select them again to upload.`;
}

/**
 * "29.99 MB": rounded DOWN to two decimals, so a file just under the 30 MB
 * limit never reads as "30 MB" next to the "30 MB or larger" warning.
 */
export function formatFileSize(bytes: number): string {
  if (!(bytes > 0)) return '0 Bytes';
  const k = 1024;
  const units = ['Bytes', 'KB', 'MB', 'GB'];
  const exponent = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
  // bytes / 1024^n is exact in floating point, so rounding down never loses a cent
  const value = Math.floor((bytes / Math.pow(k, exponent)) * 100) / 100;
  return `${value} ${units.find((_, index) => index === exponent)}`;
}

/** HH:MM in the viewer's local time, or null if the timestamp is unusable. */
export function formatResumeTime(iso?: string): string | null {
  const ms = parseTime(iso);
  return ms === null ? null : format(new Date(ms), 'HH:mm');
}

// ---------------------------------------------------------------------------
// Upload requests
// ---------------------------------------------------------------------------

interface ErrorReplyLike {
  response?: {
    status?: number;
    data?: {
      code?: unknown;
      message?: unknown;
      limit?: unknown;
      current?: unknown;
      errors?: Array<{ error?: unknown }>;
    };
  };
}

export interface UploadErrorInfo {
  message: string;
  /** The server refused the whole batch, so the files not yet sent would fail the same way. */
  stopBatch: boolean;
}

/** Message for a failed POST /reports/upload/multiple. */
export function describeUploadError(error: unknown, maxBulkFiles: number): UploadErrorInfo {
  const response = isRecord(error) ? (error as ErrorReplyLike).response : undefined;
  const data = response?.data;
  const limit = typeof data?.limit === 'number' ? data.limit : undefined;
  const current = typeof data?.current === 'number' ? data.current : undefined;

  switch (data?.code) {
    case 'BULK_DISABLED':
      return { message: 'Bulk upload is turned off. Upload one file at a time.', stopBatch: true };
    case 'BATCH_LIMIT':
      return { message: `Only ${limit ?? maxBulkFiles} files allowed per upload.`, stopBatch: true };
    case 'BAD_BATCH_ID':
      return { message: 'Upload could not be started. Please try again.', stopBatch: true };
    case 'QUEUE_LIMIT':
      return {
        message:
          current !== undefined && limit !== undefined
            ? `You already have ${current} reports waiting (limit ${limit}). Try again when some finish.`
            : 'Too many reports waiting. Try again when some finish.',
        stopBatch: true,
      };
    default:
      break;
  }

  if (response?.status === 413) return { message: 'File is too large to upload.', stopBatch: false };
  const fileError = data?.errors?.[0]?.error;
  if (typeof fileError === 'string' && fileError) return { message: fileError, stopBatch: false };
  if (typeof data?.message === 'string' && data.message) return { message: data.message, stopBatch: false };
  return { message: 'Upload failed', stopBatch: false };
}

/** One id per Upload click (uuid v4). */
export function generateUploadBatchId(): string {
  const cryptoApi = window.crypto;
  if (typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID();

  // randomUUID needs a secure context (HTTPS); getRandomValues does not.
  const bytes = new Uint8Array(16);
  cryptoApi.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // RFC 4122 variant
  let hex = '';
  bytes.forEach((byte) => {
    hex += (byte + 0x100).toString(16).slice(1);
  });
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Runs worker(0..count-1) with at most `limit` running at once. */
export async function runWithConcurrency(
  count: number,
  limit: number,
  worker: (index: number) => Promise<void>
): Promise<void> {
  let next = 0;
  const lane = async (): Promise<void> => {
    while (next < count) {
      const index = next;
      next += 1;
      try {
        await worker(index);
      } catch (error) {
        // The worker reports its own failures; one file must never stop the others.
        console.error('[BULK UPLOAD] Unexpected worker error:', error);
      }
    }
  };
  const lanes: Promise<void>[] = [];
  for (let i = 0; i < Math.min(limit, count); i += 1) lanes.push(lane());
  await Promise.all(lanes);
}

export const CAPACITY_POLL_INTERVAL_MS = 60000;

/**
 * While every unfinished report is waiting for AI capacity (possibly until
 * midnight Pacific), poll at min(60 s, time until the earliest resume), never
 * faster than the normal interval. Null when any report is queued or processing.
 */
function capacityWaitDelay(rows: BatchRow[], nowMs: number): number | null {
  const unfinished = rows.filter((row) => !row.isFinal);
  if (unfinished.length === 0 || unfinished.some((row) => row.state !== 'waiting_capacity')) return null;
  const resumeTimes = unfinished
    .map((row) => parseTime(row.resumesAt))
    .filter((ms): ms is number => ms !== null);
  if (resumeTimes.length === 0) return CAPACITY_POLL_INTERVAL_MS;
  const untilResume = Math.min(...resumeTimes) - nowMs;
  return Math.min(CAPACITY_POLL_INTERVAL_MS, Math.max(POLL_INTERVAL_MS, untilResume));
}

/**
 * Delay before the next status poll: ~5 s; 30 s while only re-checking for
 * files listed as Not uploaded; slower while only waiting for AI capacity;
 * doubling after failures (max 60 s); at least 30 s in a hidden tab.
 * `nowMs` should be the server's clock, since resumesAt comes from the server.
 */
export function nextPollDelay(
  consecutiveFailures: number,
  hidden: boolean,
  rows: BatchRow[] = [],
  nowMs: number = Date.now()
): number {
  const failureDelay =
    consecutiveFailures > 0
      ? Math.min(POLL_INTERVAL_MS * Math.pow(2, consecutiveFailures), MAX_POLL_BACKOFF_MS)
      : POLL_INTERVAL_MS;
  const baseDelay = onlyRecheckingNotUploaded(rows)
    ? NOT_UPLOADED_RECHECK_INTERVAL_MS
    : capacityWaitDelay(rows, nowMs) ?? POLL_INTERVAL_MS;
  const delay = Math.max(failureDelay, baseDelay);
  return hidden ? Math.max(delay, HIDDEN_POLL_INTERVAL_MS) : delay;
}

// ---------------------------------------------------------------------------
// In-progress upload kept in localStorage, so a page left mid-upload can show
// which selected files never reached the server
// ---------------------------------------------------------------------------

export interface StoredUploadFile {
  name: string;
  size: number;
  /** Set once the server acknowledged the file. */
  reportId?: string;
  /** Set when the upload of this file failed, with the reason shown to the nurse. */
  error?: string;
}

export interface StoredUploadBatch {
  batchId: string;
  startedAt: number;
  files: StoredUploadFile[];
  /** Heartbeat of the tab sending the files: other tabs wait while it is fresh. */
  uploadingTabId?: string;
  lastActiveAt?: number;
  /** The sending tab was hidden at its last heartbeat (its timers may then run only once a minute). */
  uploadingTabHidden?: boolean;
  /** The sending tab is being closed or reloaded: its heartbeat counts as stale at once. */
  uploadingTabClosed?: boolean;
}

export const HEARTBEAT_INTERVAL_MS = 3000;
/** A heartbeat older than this means the sending tab is gone (closed, reloaded, crashed). */
export const HEARTBEAT_STALE_MS = 15000;
/** Chrome runs the timers of a tab hidden for 5+ minutes about once a minute, so a hidden sender gets longer. */
export const HIDDEN_TAB_HEARTBEAT_STALE_MS = 2 * 60 * 1000;
/** After a complete run, other open tabs get this long to read the outcome before it is removed. */
export const FINISHED_BATCH_GRACE_MS = 60000;

/** The part of localStorage used here (tests pass an in-memory one). */
type KeyValueStorage = Pick<typeof window.localStorage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>;

/** localStorage, or null when the browser blocks it (reading window.localStorage can throw). */
function browserStorage(): KeyValueStorage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

// One record per batch, so a second tab's upload never replaces the record of the first
const userBatchPrefix = (userId: string) => `bulkUpload.inProgress.${userId}.`;
const storedBatchKey = (userId: string, batchId: string) => `${userBatchPrefix(userId)}${batchId}`;

/** This user's stored record keys (collected first: removing while walking would skip keys). */
function storedBatchKeys(userId: string, storage: KeyValueStorage | null): string[] {
  const keys: string[] = [];
  try {
    if (!storage) return keys;
    const prefix = userBatchPrefix(userId);
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && key.startsWith(prefix)) keys.push(key);
    }
  } catch {
    // Blocked storage: nothing stored
  }
  return keys;
}

// Identifies this tab in the heartbeat (not a secret)
let thisTabId: string | null = null;
const currentTabId = (): string => {
  if (!thisTabId) thisTabId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return thisTabId;
};

function isStoredUploadBatch(value: unknown): value is StoredUploadBatch {
  if (!isRecord(value) || typeof value.batchId !== 'string' || !value.batchId) return false;
  if (typeof value.startedAt !== 'number' || !Array.isArray(value.files)) return false;
  return value.files.every(
    (file) =>
      isRecord(file) &&
      typeof file.name === 'string' &&
      typeof file.size === 'number' &&
      (file.reportId === undefined || typeof file.reportId === 'string') &&
      (file.error === undefined || typeof file.error === 'string')
  );
}

function readStoredBatch(key: string, storage: KeyValueStorage | null): StoredUploadBatch | null {
  try {
    const raw = storage?.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isStoredUploadBatch(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function loadStoredBatch(userId: string, batchId: string, storage = browserStorage()): StoredUploadBatch | null {
  const batch = readStoredBatch(storedBatchKey(userId, batchId), storage);
  return batch && batch.batchId === batchId ? batch : null;
}

/** All of this user's stored batches (unreadable ones are skipped). */
export function listStoredBatches(userId: string, storage = browserStorage()): StoredUploadBatch[] {
  return storedBatchKeys(userId, storage)
    .map((key) => {
      const batch = readStoredBatch(key, storage);
      // A record must sit under its own batch's key
      return batch && storedBatchKey(userId, batch.batchId) === key ? batch : null;
    })
    .filter((batch): batch is StoredUploadBatch => batch !== null);
}

export function saveStoredBatch(userId: string, batch: StoredUploadBatch, storage = browserStorage()): void {
  try {
    storage?.setItem(storedBatchKey(userId, batch.batchId), JSON.stringify(batch));
  } catch {
    // Storage full or blocked: the upload itself still works
  }
}

/** Removes one batch's record ("Upload New Reports", finished with nothing missing, expired). */
export function clearStoredBatch(userId: string, batchId: string, storage = browserStorage()): void {
  try {
    storage?.removeItem(storedBatchKey(userId, batchId));
  } catch {
    // Nothing else to do
  }
}

/** Removes every stored batch of this user (logout: file names can be patient names). */
export function clearAllStoredBatches(userId: string, storage = browserStorage()): void {
  // Also the single record an earlier build of this page kept
  [...storedBatchKeys(userId, storage), `bulkUpload.inProgress.${userId}`].forEach((key) => {
    try {
      storage?.removeItem(key);
    } catch {
      // Nothing else to do
    }
  });
}

/** This batch's record, if it still exists (a cleared batch is never brought back). */
function loadOwnBatch(userId: string, batchId: string, storage: KeyValueStorage | null): StoredUploadBatch | null {
  return loadStoredBatch(userId, batchId, storage);
}

const pageIsHidden = (): boolean => {
  try {
    return document.hidden === true;
  } catch {
    return false;
  }
};

const withHeartbeat = (batch: StoredUploadBatch, nowMs: number): StoredUploadBatch => ({
  ...batch,
  uploadingTabId: currentTabId(),
  lastActiveAt: nowMs,
  uploadingTabHidden: pageIsHidden(),
  uploadingTabClosed: false,
});

/** Called when an upload starts: a new record for this batch (other batches' records are kept). */
export function startStoredBatch(
  userId: string,
  batchId: string,
  files: Array<{ name: string; size: number }>,
  startedAt = Date.now(),
  storage = browserStorage()
): void {
  saveStoredBatch(
    userId,
    withHeartbeat({ batchId, startedAt, files: files.map(({ name, size }) => ({ name, size })) }, startedAt),
    storage
  );
}

/** The sending tab is still alive. */
export function touchStoredBatch(userId: string, batchId: string, nowMs = Date.now(), storage = browserStorage()): void {
  const batch = loadOwnBatch(userId, batchId, storage);
  if (batch) saveStoredBatch(userId, withHeartbeat(batch, nowMs), storage);
}

/**
 * This tab is being closed or reloaded: its last heartbeat says so, so other
 * tabs stop waiting at once (instead of 15 s, or 2 min for a hidden tab).
 */
export function closeStoredHeartbeat(userId: string, batchId: string, nowMs = Date.now(), storage = browserStorage()): void {
  const batch = loadOwnBatch(userId, batchId, storage);
  if (!batch || batch.uploadingTabId !== currentTabId()) return;
  saveStoredBatch(userId, { ...batch, lastActiveAt: nowMs, uploadingTabClosed: true }, storage);
}

/** The server acknowledged file `index`. */
export function acknowledgeStoredFile(
  userId: string,
  batchId: string,
  index: number,
  reportId: string,
  storage = browserStorage(),
  nowMs = Date.now()
): void {
  const batch = loadOwnBatch(userId, batchId, storage);
  if (!batch) return;
  const files = batch.files.map((file, i) => (i === index ? { name: file.name, size: file.size, reportId } : file));
  saveStoredBatch(userId, withHeartbeat({ ...batch, files }, nowMs), storage);
}

/** The upload of file `index` failed (refused by the server, or no answer); `error` is the reason shown. */
export function failStoredFile(
  userId: string,
  batchId: string,
  index: number,
  error: string,
  storage = browserStorage(),
  nowMs = Date.now()
): void {
  const batch = loadOwnBatch(userId, batchId, storage);
  if (!batch) return;
  const files = batch.files.map((file, i) => (i === index ? { name: file.name, size: file.size, error } : file));
  saveStoredBatch(userId, withHeartbeat({ ...batch, files }, nowMs), storage);
}

/** Selected but with no outcome: neither acknowledged nor failed with a reason. */
const isMissing = (file: StoredUploadFile): boolean => !file.reportId && !file.error;

export const hasMissingFiles = (batch: StoredUploadBatch): boolean => batch.files.some(isMissing);

/**
 * The sending run ended (every file has an outcome unless the page died). The
 * heartbeat is removed at once, so other tabs stop waiting. With nothing
 * missing, the record is removed after a grace period that lets other open
 * tabs read the outcome (rejection reasons); a page closed before that clears
 * it on the next visit.
 */
export function finishStoredBatch(
  userId: string,
  batchId: string,
  storage = browserStorage(),
  graceMs = FINISHED_BATCH_GRACE_MS
): void {
  const batch = loadOwnBatch(userId, batchId, storage);
  if (!batch) return;
  const ended: StoredUploadBatch = { batchId: batch.batchId, startedAt: batch.startedAt, files: batch.files };
  saveStoredBatch(userId, ended, storage);
  if (hasMissingFiles(ended)) return;
  window.setTimeout(() => {
    const current = loadStoredBatch(userId, batchId, storage);
    if (current && !current.uploadingTabId) clearStoredBatch(userId, batchId, storage);
  }, graceMs);
}

/**
 * Some tab is still sending this batch: this one (in-app navigation away and
 * back), or another whose heartbeat is fresh: under 15 s old, or under 2 min
 * when that tab was hidden at its last heartbeat.
 */
export function isStillSending(batch: StoredUploadBatch, nowMs = Date.now()): boolean {
  return isBatchUploading(batch.batchId) || isHeartbeatAlive(batch, nowMs);
}

/** The stored heartbeat says its tab is alive: fresh, and not closed. */
export function isHeartbeatAlive(batch: StoredUploadBatch, nowMs = Date.now()): boolean {
  if (!batch.uploadingTabId || typeof batch.lastActiveAt !== 'number' || batch.uploadingTabClosed === true) {
    return false;
  }
  const staleAfter = batch.uploadingTabHidden === true ? HIDDEN_TAB_HEARTBEAT_STALE_MS : HEARTBEAT_STALE_MS;
  return nowMs - batch.lastActiveAt < staleAfter;
}

/**
 * Return visit: the stored batch to reopen on step 3, or null. Batches with
 * nothing missing, or older than 24 h, are cleared instead.
 */
export function storedBatchToReopen(userId: string, nowMs = Date.now(), storage = browserStorage()): StoredUploadBatch | null {
  const open: StoredUploadBatch[] = [];
  listStoredBatches(userId, storage).forEach((batch) => {
    if (hasMissingFiles(batch) && nowMs - batch.startedAt <= RESUME_WINDOW_MS) {
      open.push(batch);
    } else if (!isStillSending(batch, nowMs)) {
      // Nothing missing, or older than 24 h (not while a tab is still sending it)
      clearStoredBatch(userId, batch.batchId, storage);
    }
  });
  // Batches are never merged. Files lost from a batch no tab is sending come first (they need the
  // nurse); otherwise a batch another tab is still sending (its files show as Waiting). Newest first.
  const newest = (batches: StoredUploadBatch[]) =>
    batches.reduce<StoredUploadBatch | null>((best, batch) => (!best || batch.startedAt > best.startedAt ? batch : best), null);
  return newest(open.filter((batch) => !isStillSending(batch, nowMs))) ?? newest(open);
}

/**
 * Step-3 rows for a stored batch, in selection order: acknowledged files as
 * their reports (updated by the next poll), failed ones as Failed with their
 * reason, the rest as Not uploaded, or as Waiting while a tab is still sending.
 */
export function rowsFromStoredBatch(batch: StoredUploadBatch, stillSending: boolean): BatchRow[] {
  return batch.files.map((file, index) => {
    if (file.reportId) {
      return initialRowFromUpload({ reportId: file.reportId, fileName: file.name, fileSize: file.size });
    }
    if (file.error) return uploadFailureRow(index, file, file.error);
    return notUploadedRow(index, file, stillSending);
  });
}

/**
 * Brings Waiting rows up to date with the stored batch (`batch` must be the
 * tracked batch, or null if it is gone): an acknowledged file becomes its
 * report (so a later delete shows as Deleted); once no tab is sending, a failed
 * file becomes Failed with its reason and any other becomes Not uploaded.
 */
export function settlePendingRows(
  rows: BatchRow[],
  batch: StoredUploadBatch | null,
  stillSending: boolean
): BatchRow[] {
  return rows.map((row): BatchRow => {
    if (row.state !== 'upload_pending' || row.reportId) return row;
    const stored = batch?.files.find((_, index) => index === row.selectionIndex);
    if (stored?.reportId) {
      return {
        key: stored.reportId,
        reportId: stored.reportId,
        fileName: row.fileName,
        fileSize: row.fileSize,
        state: 'queued',
        isFinal: false,
      };
    }
    if (stillSending) return row;
    if (stored?.error) return { ...row, state: 'failed', isFinal: true, error: stored.error };
    return { ...row, state: 'not_uploaded', isFinal: true };
  });
}

/**
 * The view can drop the stored batch: it is the tracked batch, no tab is
 * sending it, every report is final and no file is waiting or missing.
 */
export function canClearStoredBatch(
  rows: BatchRow[],
  batch: StoredUploadBatch | null,
  trackedBatchId: string,
  stillSending: boolean
): boolean {
  if (!batch || batch.batchId !== trackedBatchId || stillSending) return false;
  if (rows.some((row) => row.state === 'not_uploaded' || row.state === 'upload_pending')) return false;
  return summariseRows(rows).allFinal;
}

// Batches this tab is still sending. Module state, so it outlives the Upload page:
// navigating away inside the app does not stop the upload.
const batchesUploading = new Set<string>();

export const isBatchUploading = (batchId: string): boolean => batchesUploading.has(batchId);

export interface BatchUploadSession {
  /** Refreshes the heartbeat (at most once a second); called on every upload progress event. */
  touch: () => void;
  /** The run is over: stops the heartbeat and the leave prompt. */
  end: () => void;
}

/**
 * While a batch is being sent: mark it as uploading, keep a heartbeat in the
 * stored batch (other tabs then show its files as Waiting), and make the
 * browser ask before the page is closed or reloaded (its own prompt, no custom
 * text). No prompt once the session has expired: the login redirect must go
 * through, and the stored batch shows what was not uploaded.
 */
export function beginBatchUpload(batchId: string, userId?: string): BatchUploadSession {
  batchesUploading.add(batchId);

  let lastTouch = 0;
  // Set once the page is going away: nothing after that may look like a living (hidden) tab
  let closing = false;
  // At most one write a second, except when the tab is shown or hidden: the hidden flag must be current
  const touch = (force = false) => {
    const now = Date.now();
    if (!userId || closing || (!force && now - lastTouch < 1000)) return;
    lastTouch = now;
    touchStoredBatch(userId, batchId, now);
  };
  const close = () => {
    if (closing) return;
    closing = true;
    if (userId) closeStoredHeartbeat(userId, batchId);
  };
  // Timers are throttled in background tabs, so progress events and visibility changes also refresh it.
  // Chrome also fires visibilitychange (hidden) while a page unloads: pagehide comes first and closes the heartbeat.
  const heartbeat = window.setInterval(() => touch(), HEARTBEAT_INTERVAL_MS);
  const onVisibilityChange = () => touch(true);
  const onPageHide = () => close();
  // Back from the back/forward cache: the page lives again
  const onPageShow = (event: { persisted?: boolean }) => {
    if (!event.persisted) return;
    closing = false;
    touch(true);
  };
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('pageshow', onPageShow);

  const onBeforeUnload = (event: { preventDefault(): void; returnValue: unknown }) => {
    let signedIn = false;
    try {
      signedIn = !!window.localStorage.getItem('token');
    } catch {
      signedIn = false;
    }
    if (!signedIn) {
      // No prompt, so the page is leaving: close the heartbeat now in case pagehide does not come.
      // (With a prompt the nurse may stay; pagehide closes it if she leaves.)
      close();
      return;
    }
    event.preventDefault();
    // Chrome and Edge only prompt when returnValue is set
    event.returnValue = '';
  };
  window.addEventListener('beforeunload', onBeforeUnload);

  return {
    touch: () => touch(),
    end: () => {
      batchesUploading.delete(batchId);
      window.clearInterval(heartbeat);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('beforeunload', onBeforeUnload);
      if (userId) finishStoredBatch(userId, batchId);
    },
  };
}

// ---------------------------------------------------------------------------
// File selection: why dropped or picked files were not added
// ---------------------------------------------------------------------------

/** Largest PDF the Upload page accepts, as today. */
export const MAX_UPLOAD_FILE_MB = 30;
export const MAX_UPLOAD_FILE_BYTES = MAX_UPLOAD_FILE_MB * 1024 * 1024;
/** The server refuses a file of exactly 30 MiB, so the page accepts at most one byte less. */
export const MAX_ACCEPTED_FILE_BYTES = MAX_UPLOAD_FILE_BYTES - 1;

/** The page's existing message for files that are not PDFs. */
export const NOT_PDF_MESSAGE = 'Please upload PDF files only';

/** A file react-dropzone did not accept, with its reasons (its FileRejection). */
export interface RejectedFile {
  file: { name: string };
  errors: ReadonlyArray<{ code: string }>;
}

export interface SelectionProblems {
  /** Names of PDFs left out only because of their size. */
  tooLarge: string[];
  /** Files that are not PDFs: refused by the drop zone, or accepted with a non-PDF type. */
  notPdf: number;
  /** Refused for any other reason (for example more than one file in single-file mode). */
  other: number;
}

export function sortOutSelection(
  rejections: ReadonlyArray<RejectedFile>,
  acceptedNotPdf: number
): SelectionProblems {
  const problems: SelectionProblems = { tooLarge: [], notPdf: acceptedNotPdf, other: 0 };
  rejections.forEach(({ file, errors }) => {
    const codes = errors.map((error) => error.code);
    // Not being a PDF is the reason that matters, even if the file is also too large
    if (codes.indexOf('file-invalid-type') !== -1) problems.notPdf += 1;
    else if (codes.indexOf('file-too-large') !== -1) problems.tooLarge.push(file.name);
    else problems.other += 1;
  });
  return problems;
}

/** "<file name> is 30 MB or larger and was not added." / "N files are 30 MB or larger and were not added." */
export function tooLargeMessage(names: string[], maxMb = MAX_UPLOAD_FILE_MB): string {
  return names.length === 1
    ? `${names[0]} is ${maxMb} MB or larger and was not added.`
    : `${names.length} files are ${maxMb} MB or larger and were not added.`;
}

/**
 * All selection warnings from one drop as one message (notistack shows at most
 * three at a time): the sentences as they are, joined with a space; a sentence
 * without an end mark gets a full stop so they read apart.
 */
export function combineSelectionWarnings(sentences: string[]): string {
  if (sentences.length === 1) return sentences[0];
  return sentences.map((sentence) => (/[.!?]$/.test(sentence) ? sentence : `${sentence}.`)).join(' ');
}
